<template>
  <div class="additional-info">
    <van-nav-bar
      :fixed="true"
      z-index="9"
      :title="$t('additionalInfo.title')"
      :border="false"
      left-arrow
      @click-left="onClickLeft"
    />

    <!-- Data Collection Terms -->
    <div class="card">
      <div class="card__title">{{ $t('additionalInfo.terms.title') }}</div>
      <div class="card__desc">{{ $t('additionalInfo.terms.desc1') }}</div>
      <div class="card__desc">{{ $t('additionalInfo.terms.desc2') }}</div>
      <div class="card__link" @click="showTermsDialog = true">
        {{ $t('additionalInfo.terms.viewFull') }}
        <van-icon name="arrow" class="card__link-arrow" />
      </div>
      <div class="line-bottom mg-t-20"></div>
      <div class="card__agree">
        <van-checkbox v-model="termsAgreed" icon-size="16px" shape="square" :disabled="submitting">
          <template>
            {{ $t('additionalInfo.terms.agreeText') }}
            <span class="privacy-link" :key="index" v-for="(ag, index) in agreements" @click="goToAgreement(ag)">{{ ag.title }}{{ index < agreements.length - 1 ? ', ' : '' }}</span> .
          </template>
        </van-checkbox>
      </div>
    </div>

    <div class="section-title">{{ $t('additionalInfo.title') }}</div>
    <div class="tip-banner">
      <div class="tip-banner__icon"><van-icon name="info-o" /></div>
      <div class="tip-banner__text">{{ $t('personalAdditionalInfo.documentsTip') }}</div>
    </div>

    <div v-if="loading" class="state-card">{{ $t('loading') }}</div>
    <div v-else-if="loadError" class="state-card state-card--error">
      <span>{{ $t('personalAdditionalInfo.loadFailed') }}</span>
      <button type="button" @click="loadPageData">{{ $t('personalAdditionalInfo.retry') }}</button>
    </div>

    <!-- Document Upload -->
    <div v-else class="card mg-t-24 documents-card">
      <div
        v-for="(doc, index) in documents"
        :key="doc.type"
      >
        <van-uploader style="width: 100%" :after-read="afterRead" :name="doc.type" :disabled="!canEditDocument(doc)" :accept="[DOC_TYPE.BANK_STATEMENT, DOC_TYPE.PAYSLIP].includes(doc.type) ? '.jpg,.jpeg,.png,.pdf' : '.jpg,.jpeg,.png'">
          <div class="doc-item" :class="{ 'is-first': index === 0 }">
            <div class="doc-item__icon">
              <img v-if="doc.uploaded && doc.fileUrl" :src="doc.fileUrl" alt="" />
              <img v-else :src="getDocIcon(doc.type)" alt="" />
            </div>
            <div class="doc-item__name">
              {{ getDocName(doc.type) }}
              <span v-if="isRequiredDocument(doc.type)" class="required-mark">*</span>
              <div class="doc-item__desc">{{ documentDescription(doc) }}</div>
            </div>
            <div class="doc-item__action">
              <span v-if="uploadingType === doc.type" class="uploading-label">{{ $t('personalAdditionalInfo.uploading') }}</span>
              <template v-else-if="doc.uploaded || isRejectedDocument(doc)">
                <div class="status-uploaded" :class="`status-uploaded--${documentStatusState(doc)}`">
                  <span class="status-badge" :class="`status-badge--${documentStatusState(doc)}`">{{ documentStatusLabel(doc) }}</span>
                </div>
                <div class="action-btn" v-if="canEditDocument(doc)">
                  <span v-if="isRejectedDocument(doc)">{{ $t('personalAdditionalInfo.reupload') }}</span>
                  <van-icon v-else name="replay" />
                </div>
              </template>
              <template v-else-if="canEditDocument(doc)">
                <div class="action-btn">
                  {{ $t('personalAdditionalInfo.upload') }}
                </div>
              </template>
            </div>
          </div>
        </van-uploader>
      </div>

    </div>

    <div class="footer-placeholder"></div>

    <!-- Submit -->
    <div class="additional-info__footer">
      <button
        type="button"
        class="btn-primary submit-button"
        :class="{ 'btn-disabled': !canSubmit }"
        :disabled="!canSubmit"
        @click="onSubmit"
      >
        {{ submitting ? $t('loading') : $t('personalAdditionalInfo.confirmSubmit') }}
      </button>
    </div>

    <van-dialog
      :value="showTermsDialog"
      @input="showTermsDialog = $event"
      :title="$t('additionalInfo.terms.title')"
      :show-cancel-button="false"
      :confirm-button-text="$t('confirm')"
    >
      <div class="terms-content">
        <p class="terms-content__item">{{ $t('additionalInfo.terms.desc1') }}</p>
        <p class="terms-content__item">{{ $t('additionalInfo.terms.desc2') }}</p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { Checkbox, Icon, Uploader, Dialog } from 'vant'
import { mapState } from 'vuex'
import routerBack from '@/mixins/routerBack'
import {
  queryAdditionalInfo,
  submitAdditionalInfo,
  DOC_TYPE, getUploadUrl, SupplementStatus
} from '@/api/additionalInfo'
import axios from "axios";
import agreement from "@/mixins/agreement";
import {getAgreements, getAppSystemConfig} from "@/api/common";
import {AGREEMENT_SCENE} from "@/common/constants";
import { DEFAULT_REG_CHANNEL } from '@/config';
import {
  buildSupplementSubmitItems,
  canSkipOrderAdditional,
  hasNewSupplementUpload,
  hasRequiredSupplementUploads,
  hasValidBpjsTkPair,
  hasValidDriverLicensePair,
  isBpjsTkType,
  isSupplementApproved,
  isSupplementEditableStatus,
  isSupplementResubmit,
  isSupplementSubmitted,
  normalizeSupplementDocuments,
  normalizeSupplementStatus,
  resolveRequiredSupplementTypes
} from '@/common/supplement'

export default {
  name: 'AdditionalInfo',
  mixins: [routerBack,agreement],
  components: {
    'van-checkbox': Checkbox,
    'van-icon': Icon,
    'van-uploader': Uploader,
    'van-dialog': Dialog.Component
  },
  data() {
    return {
      termsAgreed: false,
      documents: normalizeSupplementDocuments([]),
      submitting: false,
      overallStatus: SupplementStatus.UNSUBMIT,
      showTermsDialog: false,
      agreements: [],
      requiredDocumentTypes: [DOC_TYPE.FAMILY_CARD],
      uploadingType: '',
      loading: true,
      loadError: false,
      statusLoaded: false,
    }
  },
  computed: {
    DOC_TYPE() {
      return DOC_TYPE
    },
    ...mapState({
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL
    }),
    isOrderDetailFlow() {
      return this.$route.query.returnTo === 'orderDetail' && !!this.$route.query.orderId
    },
    canSubmit() {
      return this.statusLoaded &&
        !this.loading &&
        !this.loadError &&
        !this.uploadingType &&
        !this.submitting &&
        this.termsAgreed &&
        hasRequiredSupplementUploads(this.documents, this.requiredDocumentTypes) &&
        hasNewSupplementUpload(this.documents) &&
        hasValidDriverLicensePair(this.documents) &&
        hasValidBpjsTkPair(this.documents)
    }
  },
  created() {
    this.loadPageData()
  },
  methods: {
    isBpjsTkType,
    onClickLeft() {
      this.goBack()
    },
    documentStatusState(doc) {
      if (isSupplementApproved(doc?.status)) return 'approved'
      if (isSupplementSubmitted(doc?.status)) return 'submitted'
      if (isSupplementResubmit(doc?.status)) return 'not-submitted'
      if (doc?.uploaded && doc?.ossKey) return 'uploaded'
      return 'not-submitted'
    },
    documentStatusLabel(doc) {
      const state = this.documentStatusState(doc)
      if (state === 'uploaded') return this.$t('additionalInfo.upload.uploaded')
      return this.$t(`additionalInfo.status.${state}`)
    },
    isRejectedDocument(doc) {
      return isSupplementResubmit(doc?.status)
    },
    isRequiredDocument(type) {
      return this.requiredDocumentTypes.some(item => `${item}`.toUpperCase() === `${type}`.toUpperCase())
    },
    async loadRequiredDocumentTypes() {
      try {
        const res = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL })
        const features = res?.data?.features || res?.data || {}
        this.requiredDocumentTypes = resolveRequiredSupplementTypes(features)
      } catch (e) {
        // 与 Android 一致，配置缺失时使用家庭卡兜底。
        this.requiredDocumentTypes = resolveRequiredSupplementTypes()
      }
    },
    canEditDocument(doc) {
      return this.statusLoaded &&
        !this.loading &&
        !this.submitting &&
        !this.uploadingType &&
        isSupplementEditableStatus(doc?.status)
    },
    async loadPageData() {
      this.loading = true
      this.loadError = false
      this.statusLoaded = false
      await Promise.all([this.loadRequiredDocumentTypes(), this.queryAgreements()])
      try {
        const res = await queryAdditionalInfo()
        if (res.code !== 'Success') throw new Error(res.message || '')
        const statusData = res.data || {}
        this.overallStatus = normalizeSupplementStatus(statusData.overallStatus)
        this.documents = normalizeSupplementDocuments(statusData.items || statusData.documents || [])
        this.statusLoaded = true
        if (canSkipOrderAdditional(statusData, this.requiredDocumentTypes)) {
          this.finishOrderFlow()
        }
      } catch (e) {
        this.loadError = true
        this.$toast(e.message || this.$t('personalAdditionalInfo.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    finishOrderFlow() {
      if (this.isOrderDetailFlow) {
        this.$router.replace({
          path: '/order/detail',
          query: { orderId: this.$route.query.orderId }
        })
        return
      }
      sessionStorage.setItem('checkoutKycVerified', '1')
      this.$router.replace('/payment/checkout')
    },
    getDocName(docType) {
      const map = {
        [DOC_TYPE.FAMILY_CARD]: 'additionalInfo.docs.familyCard',
        [DOC_TYPE.SOCIAL_MEDIA]: 'additionalInfo.docs.socialMedia',
        [DOC_TYPE.BANK_STATEMENT]: 'additionalInfo.docs.bankStatement',
        [DOC_TYPE.PAYSLIP]: 'additionalInfo.docs.payslip',
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'additionalInfo.docs.driverLicenseFront',
        [DOC_TYPE.DRIVER_LICENSE_BACK]: 'additionalInfo.docs.driverLicenseBack',
        [DOC_TYPE.BPJS_TK_FRONT]: 'additionalInfo.docs.bpjsTkFront',
        [DOC_TYPE.BPJS_TK_BACK]: 'additionalInfo.docs.bpjsTkBack',
      }
      return this.$t(map[docType] || docType)
    },
    documentDescription(doc) {
      if (this.isRejectedDocument(doc)) {
        return doc.rejectReason || doc.reason || doc.remark || this.$t('personalAdditionalInfo.rejectedDocumentDesc')
      }
      if (isSupplementApproved(doc?.status)) {
        const approvedMap = {
          [DOC_TYPE.FAMILY_CARD]: 'personalAdditionalInfo.docs.familyCardVerifiedDesc',
          [DOC_TYPE.SOCIAL_MEDIA]: 'personalAdditionalInfo.docs.socialMediaVerifiedDesc',
          [DOC_TYPE.BANK_STATEMENT]: 'personalAdditionalInfo.docs.bankStatementVerifiedDesc',
          [DOC_TYPE.PAYSLIP]: 'personalAdditionalInfo.docs.payslipVerifiedDesc',
          [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'personalAdditionalInfo.docs.driverLicenseVerifiedDesc',
          [DOC_TYPE.DRIVER_LICENSE_BACK]: 'personalAdditionalInfo.docs.driverLicenseVerifiedDesc',
          [DOC_TYPE.BPJS_TK_FRONT]: 'personalAdditionalInfo.docs.bpjsTkVerifiedDesc',
          [DOC_TYPE.BPJS_TK_BACK]: 'personalAdditionalInfo.docs.bpjsTkVerifiedDesc'
        }
        return this.$t(approvedMap[doc.type] || 'additionalInfo.upload.uploaded')
      }
      if (doc?.uploaded) {
        return doc.type === DOC_TYPE.BANK_STATEMENT
          ? this.$t('additionalInfo.upload.uploadedBankStatement')
          : this.$t('additionalInfo.upload.uploaded')
      }
      const map = {
        [DOC_TYPE.FAMILY_CARD]: 'personalAdditionalInfo.docs.familyCardDesc',
        [DOC_TYPE.SOCIAL_MEDIA]: 'personalAdditionalInfo.docs.socialMediaDesc',
        [DOC_TYPE.BANK_STATEMENT]: 'personalAdditionalInfo.docs.bankStatementDesc',
        [DOC_TYPE.PAYSLIP]: 'personalAdditionalInfo.docs.payslipDesc',
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: 'personalAdditionalInfo.docs.driverLicenseFrontDesc',
        [DOC_TYPE.DRIVER_LICENSE_BACK]: 'personalAdditionalInfo.docs.driverLicenseBackDesc',
        [DOC_TYPE.BPJS_TK_FRONT]: 'personalAdditionalInfo.docs.bpjsTkDesc',
        [DOC_TYPE.BPJS_TK_BACK]: 'personalAdditionalInfo.docs.bpjsTkDesc'
      }
      return this.$t(map[doc.type] || '')
    },
    getDocIcon(docType) {
      // 复用项目内已有图标占位
      const map = {
        [DOC_TYPE.FAMILY_CARD]: require('@/assets/img/additional-info/family_card_icon.png'),
        [DOC_TYPE.SOCIAL_MEDIA]: require('@/assets/img/additional-info/social_media_icon.png'),
        [DOC_TYPE.BANK_STATEMENT]: require('@/assets/img/additional-info/bank_statement_icon.png'),
        [DOC_TYPE.PAYSLIP]: require('@/assets/img/additional-info/payslip_icon.png'),
        [DOC_TYPE.DRIVER_LICENSE_FRONT]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.DRIVER_LICENSE_BACK]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.BPJS_TK_FRONT]: require('@/assets/img/additional-info/drivers_license_icon.png'),
        [DOC_TYPE.BPJS_TK_BACK]: require('@/assets/img/additional-info/drivers_license_icon.png')
      }
      return map[docType] || require('@/assets/img/additional-info/family_card_icon.png')
    },
    async onSubmit() {
      if (!this.statusLoaded || this.loading || this.loadError || this.submitting || this.uploadingType) return
      if (!this.termsAgreed) return this.$toast(this.$t('additionalInfo.terms.notAgreedTip'))
      if (!hasRequiredSupplementUploads(this.documents, this.requiredDocumentTypes)) {
        return this.$toast(this.$t('additionalInfo.requiredDocumentsTip'))
      }
      if (!hasNewSupplementUpload(this.documents)) {
        return this.$toast(this.$t('additionalInfo.uploadAtLeastOneTip'))
      }
      if (!hasValidDriverLicensePair(this.documents)) {
        return this.$toast(this.$t('additionalInfo.driverLicensePairTip'))
      }
      if (!hasValidBpjsTkPair(this.documents)) {
        return this.$toast(this.$t('additionalInfo.bpjsTkPairTip'))
      }
      this.submitting = true
      try {
        const res = await submitAdditionalInfo({ items: buildSupplementSubmitItems(this.documents) })
        if (res.code === 'Success') {
          this.finishOrderFlow()
        } else {
          this.$toast({
            message: res.message || this.$t('additionalInfo.submitFailed'),
            duration: 5000
          })
        }
      } catch (e) {
        this.$toast({
          message: this.$t('additionalInfo.submitFailed'),
          duration: 5000
        })
      } finally {
        this.submitting = false
      }
    },
    async afterRead(file, name) {
      const type = name.name
      const target = this.documents.find(document => document.type === type)
      if (!file || !file.file || !target || !this.canEditDocument(target)) return
      const allowsPdf = [DOC_TYPE.BANK_STATEMENT, DOC_TYPE.PAYSLIP].includes(type)
      const allowedExtensions = allowsPdf ? /\.(jpe?g|png|pdf)$/i : /\.(jpe?g|png)$/i
      if (!allowedExtensions.test(file.file.name)) {
        return this.$toast(this.$t('additionalInfo.fileTypeTip', { formats: allowsPdf ? 'JPG, JPEG, PNG, PDF' : 'JPG, JPEG, PNG' }))
      }
      const maxSizeMB = /\.pdf$/i.test(file.file.name) ? 10 : 2
      const maxSize = maxSizeMB * 1024 * 1024
      if (file.file.size > maxSize) {
        this.$toast({
          message:this.$t('additionalInfo.upload.fileTooLarge', { size: maxSizeMB }),
          duration: 5000
        })
        return
      }
      this.uploadingType = type
      try {
        const resp = await getUploadUrl({ type, fileName: file.file.name })
        if (resp.code !== 'Success' || !resp.data) throw new Error(resp.message || '')
        const data = resp.data
        const host = data.host
        const ossFormData = new FormData()
        ossFormData.append('policy', data.policy)
        ossFormData.append('OSSAccessKeyId', data.OSSAccessKeyId)
        ossFormData.append('signature', data.signature)
        ossFormData.append('key', data.key)
        ossFormData.append('Content-Type', data.contentType)
        ossFormData.append('success_action_status', '200')
        ossFormData.append('file', file.file)
        await axios.post(host, ossFormData, {})
        this.$set(target, 'uploaded', true)
        this.$set(target, 'ossKey', data.key)
        this.$set(target, 'status', SupplementStatus.UNSUBMIT)
      } catch (e) {
        this.$toast({
          message: this.$t('additionalInfo.upload.failed'),
          duration: 5000
        })
      } finally {
        this.uploadingType = ''
      }
    },
    async queryAgreements() {
      try {
        const res = await getAgreements({ sceneCode: AGREEMENT_SCENE.ADDITIONAL_INFO })
        if (res.code === 'Success') {
          this.agreements = res.data.agreements || []
        }
      } catch (e) {
        // 协议接口失败不清空已经加载成功的协议。
      }
    },
  }
}
</script>

<style lang="less" scoped>
.additional-info {
  min-height: 100vh;
  padding: 88px 24px 200px;
  background: var(--bg-color);
  box-sizing: border-box;

  .card {
    background: #fff;
    border-radius: 24px;
    padding: 32px 28px;
    margin-top: 24px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.06);

    &:first-of-type {
      margin-top: 24px;
    }

    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 32px;
      font-weight: 600;
      color: var(--text-color-black);
      line-height: 44px;
    }

    &__desc {
      margin-top: 20px;
      font-size: 26px;
      line-height: 38px;
      color: #666;
    }

    &__link {
      margin-top: 24px;
      font-size: 26px;
      line-height: 36px;
      color: var(--primary-color);
      display: inline-flex;
      align-items: center;

      &-arrow {
        margin-left: 6px;
        font-size: 24px;
      }
    }

    &__agree {
      margin-top: 30px;
      font-size: 24px;
      line-height: 34px;

      .privacy-link {
        color: var(--link-color);
      }

      ::v-deep .van-checkbox {
        align-items: flex-start;
      }

      ::v-deep .van-checkbox__icon {
        margin-top: 4px;
      }

      ::v-deep .van-checkbox__label {
        margin-left: 42px;
        font-size: 24px;
        line-height: 34px;
        color: var(--text-color-gray);
      }
    }
  }

  .section-title {
    margin: 30px 4px 16px;
    font-size: 32px;
    line-height: 44px;
    font-weight: 600;
    color: var(--text-color-black);
  }

  .state-card {
    margin-top: 24px;
    padding: 32px 28px;
    border-radius: 20px;
    background: #fff;
    color: #6F7788;
    font-size: 26px;
    line-height: 38px;
    text-align: center;

    &--error button {
      margin-left: 16px;
      padding: 8px 18px;
      border: 2px solid var(--primary-color);
      border-radius: 14px;
      background: #fff;
      color: var(--primary-color);
    }
  }

  .doc-item {
    width: 100%;
    position: relative;
    margin-top: 24px;
    padding: 20px 20px;
    background: #F8FAFE;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;

    &.is-first {
      margin-top: 28px;
    }

    &__icon {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;

      img {
        max-width: 70%;
        max-height: 70%;
        object-fit: cover;
      }
    }

    &__name {
      flex: 1;
      margin-left: 24px;
      font-size: 28px;
      font-weight: 500;
      color: var(--text-color-black);
      line-height: 38px;
      word-break: break-word;
    }
    .doc-item__desc { margin-top: 6px; color: #A7ADBB; font-size: 23px; line-height: 32px; font-weight: 400; }

    .status-badge {
      padding: 6px 14px;
      border-radius: 14px;
      font-size: 22px;
      line-height: 30px;
      font-weight: 500;
      white-space: nowrap;
    }

    .status-badge--not-submitted { color: #F04438; background: #FFF0F0; }
    .status-badge--submitted { color: #B26A00; background: #FFF2D9; }
    .status-badge--approved { color: #008A3E; background: #E1F7EA; }
    .status-badge--uploaded { color: #00A957; background: #E5F8ED; }

    .required-mark {
      margin-left: 6px;
      color: #F04438;
      font-weight: 600;
    }

    &__action {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      gap: 10px;
      flex-shrink: 0;
    }

    &__file {
      position: absolute;
      width: 0;
      height: 0;
      opacity: 0;
      overflow: hidden;
      pointer-events: none;
    }

    .status-uploaded { display: flex; align-items: center; }
    .action-btn { padding: 10px 18px; border: 2px solid var(--primary-color); border-radius: 16px; color: var(--primary-color); font-size: 24px; line-height: 30px; white-space: nowrap; }
    .uploading-label { color: var(--primary-color); font-size: 22px; line-height: 30px; white-space: nowrap; }

  }

  .tip-banner {
    margin-top: 24px;
    padding: 24px 28px;
    background: #EAF1FF;
    border-radius: 16px;
    display: flex;
    align-items: flex-start;

    &__icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--primary-color);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      margin-top: 4px;
    }

    &__text {
      margin-left: 20px;
      font-size: 26px;
      line-height: 38px;
      color: #4A5A7A;
      flex: 1;
    }
  }

  .footer-placeholder {
    height: 40px;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 24px 32px 40px;
    background: var(--bg-color);
    z-index: 10;
  }

  .submit-button { display: block; width: 100%; padding: 0; border: 0; }

  ::v-deep .van-uploader__input-wrapper {
    width: 100% !important;
  }
  ::v-deep .van-uploader__wrapper {
    width: 100% !important;
  }
  ::v-deep .van-uploader__wrapper--disabled {
    opacity: 1 !important;
  }
  ::v-deep .van-uploader__mask {
    display: none !important;
  }
}

.terms-content {
  padding: 16px 24px 24px;

  &__item {
    font-size: 26px;
    line-height: 38px;
    color: #666;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
